/**
 * Message component for rendering chat messages
 */

import type { ChatMessage, WidgetFeatures, GreetingBlock } from '../core/config';
import { renderBlock } from './blocks';
import { isUrlSafe, escapeHtml as escapeHtmlUtil } from './blocks/utils';
import { t } from '../core/i18n';
import { SpeechSynthesisService } from '../core/voice/speech-synthesis';
import { isSpeechSynthesisSupported } from '../core/voice/feature-detection';
import { createActivityLoader } from '../core/loading/activity-loader';

/**
 * Options for rendering messages
 */
export interface MessageRenderOptions {
  /** Feature flags from backend config */
  features?: WidgetFeatures;
  /** Action handler for interactive blocks */
  onAction?: (value: string) => void;
}

/**
 * Format timestamp for display
 */
function formatTime(date: Date): string {
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

/**
 * Escape HTML to prevent XSS
 * Delegates to shared utility function
 */
function escapeHtml(text: string): string {
  return escapeHtmlUtil(text);
}

// isUrlSafe is imported from blocks/utils

/**
 * Parse markdown-like formatting in text
 */
function parseMarkdown(text: string): string {
  // Escape HTML first
  let html = escapeHtml(text);

  // Bold: **text** or __text__
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/__(.+?)__/g, '<strong>$1</strong>');

  // Italic: *text* or _text_
  html = html.replace(/\*(.+?)\*/g, '<em>$1</em>');
  html = html.replace(/_(.+?)_/g, '<em>$1</em>');

  // Code: `text`
  html = html.replace(/`(.+?)`/g, '<code>$1</code>');

  // Links: [text](url) - validate URL before creating link
  html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_match, linkText, url) => {
    if (isUrlSafe(url)) {
      return `<a href="${escapeHtml(url)}" target="_blank" rel="noopener noreferrer">${linkText}</a>`;
    }
    // Strip unsafe link, keep text
    return linkText;
  });

  // Line breaks
  html = html.replace(/\n/g, '<br>');

  return html;
}

/**
 * Render a user message
 */
export function renderUserMessage(message: ChatMessage, container: HTMLElement): HTMLElement {
  const wrapper = document.createElement('div');
  wrapper.className = 'pms-message pms-message-user';
  wrapper.dataset['messageId'] = message.id;

  const bubble = document.createElement('div');
  bubble.className = 'pms-message-bubble';
  bubble.innerHTML = parseMarkdown(message.content);

  const time = document.createElement('div');
  time.className = 'pms-message-time';
  time.textContent = formatTime(message.timestamp);

  wrapper.appendChild(bubble);
  wrapper.appendChild(time);
  container.appendChild(wrapper);

  return wrapper;
}

/**
 * Render an assistant message
 */
export function renderAssistantMessage(
  message: ChatMessage,
  container: HTMLElement,
  options?: MessageRenderOptions
): HTMLElement {
  const wrapper = document.createElement('div');
  wrapper.className = 'pms-message pms-message-assistant';
  wrapper.dataset['messageId'] = message.id;

  // Avatar
  const avatar = document.createElement('div');
  avatar.className = 'pms-message-avatar';
  avatar.innerHTML = `
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
      <circle cx="12" cy="12" r="10"/>
      <path d="M12 16v-4M12 8h.01"/>
    </svg>
  `;

  const content = document.createElement('div');
  content.className = 'pms-message-content';

  // Text content
  if (message.content) {
    const textWrapper = document.createElement('div');
    textWrapper.className = 'pms-message-text';
    textWrapper.innerHTML = parseMarkdown(message.content);
    content.appendChild(textWrapper);
  }

  // Render blocks if present
  if (message.blocks && message.blocks.length > 0) {
    // Add wide class for full-width blocks (fallback for browsers without :has())
    wrapper.classList.add('pms-message-wide');

    const blocksWrapper = document.createElement('div');
    blocksWrapper.className = 'pms-message-blocks';

    for (const block of message.blocks) {
      renderBlock(block, blocksWrapper, options?.onAction);
    }

    content.appendChild(blocksWrapper);
  }

  // Timestamp and feedback row
  const footer = document.createElement('div');
  footer.className = 'pms-message-footer';

  const time = document.createElement('div');
  time.className = 'pms-message-time';
  time.textContent = formatTime(message.timestamp);
  footer.appendChild(time);

  // Action buttons (copy, feedback) - respect features flags
  const showFeedback = options?.features?.feedbackEnabled !== false; // Default true
  const actions = createMessageActions(message.id, message.content, showFeedback);
  footer.appendChild(actions);

  content.appendChild(footer);

  wrapper.appendChild(avatar);
  wrapper.appendChild(content);
  container.appendChild(wrapper);

  return wrapper;
}

// TTS service singleton for read aloud
let ttsService: SpeechSynthesisService | null = null;

function getTtsService(): SpeechSynthesisService | null {
  if (!isSpeechSynthesisSupported()) return null;
  if (!ttsService) {
    ttsService = new SpeechSynthesisService();
  }
  return ttsService;
}

/**
 * Create action buttons for a message (copy, read aloud, feedback)
 * @param messageId - The message ID for tracking
 * @param content - The message content for copy/read
 * @param showFeedback - Whether to show feedback buttons (default: true)
 */
function createMessageActions(messageId: string, content: string, showFeedback = true): HTMLElement {
  const container = document.createElement('div');
  container.className = 'pms-message-actions';
  container.dataset['messageId'] = messageId;

  // Copy button (always shown)
  const copyBtn = document.createElement('button');
  copyBtn.className = 'pms-action-btn pms-copy-btn';
  copyBtn.type = 'button';
  copyBtn.title = t('messages.copyToClipboard');
  copyBtn.setAttribute('aria-label', t('messages.copyToClipboard'));
  copyBtn.dataset['content'] = content;
  copyBtn.innerHTML = `
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
    </svg>
  `;
  container.appendChild(copyBtn);

  // Read aloud button (only if TTS supported)
  if (isSpeechSynthesisSupported()) {
    const readBtn = document.createElement('button');
    readBtn.className = 'pms-action-btn pms-read-btn';
    readBtn.type = 'button';
    readBtn.title = t('voice.readAloud');
    readBtn.setAttribute('aria-label', t('voice.readAloud'));
    readBtn.dataset['content'] = content;
    readBtn.innerHTML = `
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
        <path d="M15.54 8.46a5 5 0 0 1 0 7.07"/>
        <path d="M19.07 4.93a10 10 0 0 1 0 14.14"/>
      </svg>
    `;
    readBtn.addEventListener('click', () => {
      const tts = getTtsService();
      if (!tts) return;

      if (tts.isSpeaking) {
        tts.stop();
        readBtn.classList.remove('pms-speaking');
        readBtn.title = t('voice.readAloud');
      } else {
        tts.speak(content, {
          onEnd: () => {
            readBtn.classList.remove('pms-speaking');
            readBtn.title = t('voice.readAloud');
          },
          onError: () => {
            readBtn.classList.remove('pms-speaking');
            readBtn.title = t('voice.readAloud');
          }
        });
        readBtn.classList.add('pms-speaking');
        readBtn.title = t('voice.stopReading');
      }
    });
    container.appendChild(readBtn);
  }

  // Feedback buttons (only shown if feedbackEnabled)
  if (showFeedback) {
    // Thumbs up button
    const thumbsUp = document.createElement('button');
    thumbsUp.className = 'pms-action-btn pms-feedback-positive';
    thumbsUp.type = 'button';
    thumbsUp.title = t('messages.goodResponse');
    thumbsUp.setAttribute('aria-label', t('messages.goodResponse'));
    thumbsUp.dataset['rating'] = 'positive';
    thumbsUp.innerHTML = `
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"/>
      </svg>
    `;

    // Thumbs down button
    const thumbsDown = document.createElement('button');
    thumbsDown.className = 'pms-action-btn pms-feedback-negative';
    thumbsDown.type = 'button';
    thumbsDown.title = t('messages.poorResponse');
    thumbsDown.setAttribute('aria-label', t('messages.poorResponse'));
    thumbsDown.dataset['rating'] = 'negative';
    thumbsDown.innerHTML = `
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3zm7-13h2.67A2.31 2.31 0 0 1 22 4v7a2.31 2.31 0 0 1-2.33 2H17"/>
      </svg>
    `;

    container.appendChild(thumbsUp);
    container.appendChild(thumbsDown);
  }

  return container;
}

/**
 * Render a message (auto-detects type)
 */
export function renderMessage(
  message: ChatMessage,
  container: HTMLElement,
  options?: MessageRenderOptions
): HTMLElement {
  if (message.role === 'user') {
    return renderUserMessage(message, container);
  } else {
    return renderAssistantMessage(message, container, options);
  }
}

/**
 * Create typing indicator element.
 * Uses the unified activity loader (ping-pong animation).
 */
export function createTypingIndicator(statusText?: string): HTMLElement {
  return createActivityLoader(statusText);
}

/**
 * Update the status text of a typing indicator
 */
export function updateTypingStatus(indicator: HTMLElement, statusText: string): void {
  let status = indicator.querySelector('.pms-activity-loader-text') as HTMLElement;
  if (!status) {
    status = document.createElement('span');
    status.className = 'pms-activity-loader-text';
    indicator.querySelector('.pms-activity-loader-inner')?.appendChild(status);
  }
  status.textContent = statusText;
}

/**
 * Get the welcome icon SVG based on vertical.
 * Each vertical gets a signature icon that matches its metaphor.
 */
function getWelcomeIconSvg(vertical?: string): string {
  switch (vertical) {
    case 'saas':
    case 'tech':
      // Code brackets </> — developer/terminal metaphor
      return `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <polyline points="16 18 22 12 16 6"/>
        <polyline points="8 6 2 12 8 18"/>
      </svg>`;
    case 'restaurant':
      // Fork and knife — warm bistro metaphor
      return `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M3 2v7c0 1.1.9 2 2 2h4v7c0 1.7 1.3 3 3 3s3-1.3 3-3v-6"/>
        <line x1="7" y1="2" x2="7" y2="11"/>
        <line x1="3" y1="6" x2="11" y2="6"/>
        <path d="M17 2c0 4.4 2 8 2 8s2-3.6 2-8"/>
      </svg>`;
    case 'ecommerce':
      // Shopping bag — storefront metaphor
      return `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/>
        <line x1="3" y1="6" x2="21" y2="6"/>
        <path d="M16 10a4 4 0 0 1-8 0"/>
      </svg>`;
    case 'healthcare':
      // Heart pulse — clinical care metaphor
      return `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M22 12h-4l-3 9L9 3l-3 9H2"/>
      </svg>`;
    case 'finance':
    case 'fintech':
      // Shield with checkmark — security/trust metaphor
      return `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
        <polyline points="9 12 11 14 15 10"/>
      </svg>`;
    case 'real_estate':
      // House — property/home metaphor
      return `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
        <polyline points="9 22 9 12 15 12 15 22"/>
      </svg>`;
    case 'automotive':
      // Gauge/speedometer — performance metaphor
      return `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <circle cx="12" cy="12" r="10"/>
        <path d="M16.24 7.76l-2.12 6.36-6.36 2.12"/>
      </svg>`;
    case 'portfolio':
      // Pen tool — creative/design metaphor
      return `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M12 19l7-7 3 3-7 7-3-3z"/>
        <path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z"/>
        <path d="M2 2l7.586 7.586"/>
        <circle cx="11" cy="11" r="2"/>
      </svg>`;
    case 'professional_services':
      // Briefcase — professional/business metaphor
      return `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <rect x="2" y="7" width="20" height="14" rx="2" ry="2"/>
        <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
      </svg>`;
    case 'dental':
      // Tooth — friendly molar icon for dental practices
      return `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M7 4c-1.5 0-3 1.5-3 4 0 3 1 5 2 8 .5 1.5 1 3 2 3s1.5-1 2-3c.5 2 1 3 2 3s1.5-1.5 2-3c1-3 2-5 2-8 0-2.5-1.5-4-3-4-1 0-2 .5-2 1.5 0-1-1-1.5-2-1.5z"/>
      </svg>`;
    case 'education':
      // Graduation cap — academic/scholarly metaphor
      return `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M22 10l-10-5L2 10l10 5 10-5z"/>
        <path d="M6 12v5c0 1.7 2.7 3 6 3s6-1.3 6-3v-5"/>
        <line x1="22" y1="10" x2="22" y2="16"/>
      </svg>`;
    case 'nonprofit':
      // Globe with heart — community/impact metaphor
      return `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <circle cx="12" cy="12" r="10"/>
        <path d="M2 12h20"/>
        <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
        <path d="M12 8c-.5-.7-1.5-1-2.2-.7-.7.3-1.1 1-.8 1.7.2.4.5.8 1 1.2l2 1.8 2-1.8c.5-.4.8-.8 1-1.2.3-.7-.1-1.4-.8-1.7-.7-.3-1.7 0-2.2.7z" fill="currentColor" stroke="none"/>
      </svg>`;
    case 'legal':
      // Scales of justice — law/authority metaphor
      return `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <line x1="12" y1="3" x2="12" y2="21"/>
        <line x1="4" y1="7" x2="20" y2="7"/>
        <path d="M4 7l-1 7h6L8 7"/>
        <path d="M20 7l-1 7h-6l1-7"/>
        <line x1="8" y1="21" x2="16" y2="21"/>
      </svg>`;
    default:
      // Chat bubble — universal default
      return `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
      </svg>`;
  }
}

/**
 * Create welcome message element with optional greeting blocks and suggestion pills
 *
 * Greeting blocks allow showcasing rich UI components (stats, interactive actions, etc.)
 * immediately when the panel opens - zero user interaction required.
 */
export function createWelcomeMessage(
  greeting: string,
  suggestions?: string[],
  onSuggestionClick?: (suggestion: string) => void,
  greetingBlocks?: GreetingBlock[],
  onActionClick?: (action: string) => void,
  vertical?: string
): HTMLElement {
  const wrapper = document.createElement('div');
  wrapper.className = 'pms-welcome-message';

  const icon = document.createElement('div');
  icon.className = 'pms-welcome-icon';
  icon.innerHTML = getWelcomeIconSvg(vertical);

  const text = document.createElement('div');
  text.className = 'pms-welcome-text';
  text.innerHTML = parseMarkdown(greeting);

  wrapper.appendChild(icon);
  wrapper.appendChild(text);

  // Render greeting blocks if provided (STATS, INTERACTIVE, etc.)
  // These display rich components immediately, before any user interaction
  if (greetingBlocks && greetingBlocks.length > 0) {
    const blocksWrapper = document.createElement('div');
    blocksWrapper.className = 'pms-greeting-blocks';

    for (const greetingBlock of greetingBlocks) {
      // Greeting blocks have same format as MessageBlock: {type, data}
      const messageBlock = {
        type: greetingBlock.type,
        data: greetingBlock.data
      };
      renderBlock(messageBlock, blocksWrapper, onActionClick);
    }

    wrapper.appendChild(blocksWrapper);
  }

  // Add suggestion pills only when no greeting blocks exist.
  // Greeting blocks (card-grid, interactive, etc.) already provide clickable actions,
  // so showing pills alongside them is redundant.
  const hasGreetingBlocks = greetingBlocks && greetingBlocks.length > 0;
  if (suggestions && suggestions.length > 0 && !hasGreetingBlocks) {
    const suggestionsWrapper = document.createElement('div');
    suggestionsWrapper.className = 'pms-suggestions';

    for (const suggestion of suggestions) {
      const pill = document.createElement('button');
      pill.className = 'pms-suggestion';
      pill.type = 'button';

      // SaaS: add command prefix like interactive block actions
      if (vertical === 'saas' || vertical === 'tech') {
        const prefix = document.createElement('span');
        prefix.className = 'pms-suggestion-prefix';
        prefix.textContent = '$ ';
        pill.appendChild(prefix);
      }

      // Education: question prefix like a quiz prompt
      if (vertical === 'education') {
        const prefix = document.createElement('span');
        prefix.className = 'pms-suggestion-prefix';
        prefix.textContent = 'Q: ';
        pill.appendChild(prefix);
      }

      // Nonprofit: action arrow like a call-to-action
      if (vertical === 'nonprofit') {
        const prefix = document.createElement('span');
        prefix.className = 'pms-suggestion-prefix';
        prefix.textContent = '\u2192 ';
        pill.appendChild(prefix);
      }

      // Legal: section symbol prefix
      if (vertical === 'legal') {
        const prefix = document.createElement('span');
        prefix.className = 'pms-suggestion-prefix';
        prefix.textContent = '\u00A7 ';
        pill.appendChild(prefix);
      }

      // Restaurant: menu bullet prefix
      if (vertical === 'restaurant') {
        const prefix = document.createElement('span');
        prefix.className = 'pms-suggestion-prefix';
        prefix.textContent = '\u25E6 ';
        pill.appendChild(prefix);
      }

      // Ecommerce: browse chevron prefix
      if (vertical === 'ecommerce') {
        const prefix = document.createElement('span');
        prefix.className = 'pms-suggestion-prefix';
        prefix.textContent = '\u203A ';
        pill.appendChild(prefix);
      }

      // Healthcare: medical plus prefix
      if (vertical === 'healthcare') {
        const prefix = document.createElement('span');
        prefix.className = 'pms-suggestion-prefix';
        prefix.textContent = '+ ';
        pill.appendChild(prefix);
      }

      // Finance: ticker arrow prefix
      if (vertical === 'finance' || vertical === 'fintech') {
        const prefix = document.createElement('span');
        prefix.className = 'pms-suggestion-prefix';
        prefix.textContent = '\u25B8 ';
        pill.appendChild(prefix);
      }

      // Real Estate: compass diamond prefix
      if (vertical === 'real_estate') {
        const prefix = document.createElement('span');
        prefix.className = 'pms-suggestion-prefix';
        prefix.textContent = '\u25C8 ';
        pill.appendChild(prefix);
      }

      // Automotive: speed arrow prefix
      if (vertical === 'automotive') {
        const prefix = document.createElement('span');
        prefix.className = 'pms-suggestion-prefix';
        prefix.textContent = '\u25B6 ';
        pill.appendChild(prefix);
      }

      // Portfolio: creative tilde prefix
      if (vertical === 'portfolio') {
        const prefix = document.createElement('span');
        prefix.className = 'pms-suggestion-prefix';
        prefix.textContent = '~ ';
        pill.appendChild(prefix);
      }

      // Professional Services: formal bullet prefix
      if (vertical === 'professional_services') {
        const prefix = document.createElement('span');
        prefix.className = 'pms-suggestion-prefix';
        prefix.textContent = '\u2022 ';
        pill.appendChild(prefix);
      }

      // Dental: sparkle prefix
      if (vertical === 'dental') {
        const prefix = document.createElement('span');
        prefix.className = 'pms-suggestion-prefix';
        prefix.textContent = '\u2726 ';
        pill.appendChild(prefix);
      }

      // Agency: broadcast prefix
      if (vertical === 'agency') {
        const prefix = document.createElement('span');
        prefix.className = 'pms-suggestion-prefix';
        prefix.textContent = '\u00BB ';
        pill.appendChild(prefix);
      }

      // Fun: terminal prompt prefix
      if (vertical === 'fun') {
        const prefix = document.createElement('span');
        prefix.className = 'pms-suggestion-prefix';
        prefix.textContent = '> ';
        pill.appendChild(prefix);
      }

      const label = document.createElement('span');
      label.textContent = suggestion;
      pill.appendChild(label);

      if (onSuggestionClick) {
        pill.addEventListener('click', () => {
          // Disable all pills after click
          suggestionsWrapper.querySelectorAll('.pms-suggestion').forEach((btn) => {
            (btn as HTMLButtonElement).disabled = true;
          });
          onSuggestionClick(suggestion);
        });
      }

      suggestionsWrapper.appendChild(pill);
    }

    wrapper.appendChild(suggestionsWrapper);
  }

  return wrapper;
}

/**
 * Create error message element
 */
export function createErrorMessage(error: string): HTMLElement {
  const wrapper = document.createElement('div');
  wrapper.className = 'pms-message pms-message-error';

  const icon = document.createElement('div');
  icon.className = 'pms-error-icon';
  icon.innerHTML = `
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <circle cx="12" cy="12" r="10"/>
      <line x1="12" y1="8" x2="12" y2="12"/>
      <line x1="12" y1="16" x2="12.01" y2="16"/>
    </svg>
  `;

  const content = document.createElement('div');
  content.className = 'pms-error-content';
  content.textContent = error;

  wrapper.appendChild(icon);
  wrapper.appendChild(content);

  return wrapper;
}

/**
 * Create usage limit message element.
 * Shown when the workspace has exceeded its monthly message limit.
 * Uses a softer amber style instead of error red.
 */
export function createUsageLimitMessage(): HTMLElement {
  const wrapper = document.createElement('div');
  wrapper.className = 'pms-message pms-message-limit';

  const icon = document.createElement('div');
  icon.className = 'pms-limit-icon';
  icon.innerHTML = `
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <circle cx="12" cy="12" r="10"/>
      <path d="M12 8v4"/>
      <path d="M12 16h.01"/>
    </svg>
  `;

  const content = document.createElement('div');
  content.className = 'pms-limit-content';
  content.textContent = 'This assistant has reached its free message limit. The site owner can upgrade their plan to restore access.';

  wrapper.appendChild(icon);
  wrapper.appendChild(content);

  return wrapper;
}
